// core
import React, { ReactElement } from "react";
import NumberFormat from "react-number-format";

// components
import Icon from "../../Icon/Icon";

// interfaces
import { IIconProps } from "../../../interfaces/IIconProps";

// styles
import cx from "classnames";
import styles from "./Input.module.scss";
import InputWrapper from "../InputWrapper/InputWrapper";
import IBaseFormComponentProps from "../../../interfaces/IBaseFormComponentProps";

interface IInput<T extends HTMLElement, V extends unknown>
    extends IBaseFormComponentProps<T, V> {
    id?: string;
    hasIcon?: boolean;
    hasCurrency?: boolean;
    placeholder?: string;
    iconProps?: IIconProps;

    // noMargin?: boolean;
}

interface IInputNumber extends Omit<IInput<HTMLInputElement, number>, "onChange"> {
    type: "number";
    onChange?: (e: number) => void;
    textRef?: React.Ref<HTMLInputElement>;
}

interface IInputText extends IInput<HTMLInputElement, string> {
    type: "text" | "email" | "tel";
    textRef?: React.Ref<HTMLInputElement>;
}

interface IInputTextArea extends IInput<HTMLTextAreaElement, string> {
    type: "textarea";
    textRef?: React.Ref<HTMLTextAreaElement>;
    // Text Area
    rows?: number;
    maxlength?: number;
}

type InputProps = IInputNumber | IInputText | IInputTextArea;

const renderInputElement = (props: InputProps): ReactElement => {
    const { id, name, type, placeholder, hasError } = props;

    const baseClasses = cx(styles["inputwrapper-textinput"], {
        [styles["inputwrapper--error"]]: hasError
    });

    if (props.type === "number") {
        return (
            <NumberFormat
                id={id}
                name={name}
                className={baseClasses}
                // If undefined of null is passed, then is stays as the last value.
                // So we need to pass in an empty string to clear the field in those cases.
                value={isNaN(props.value) ? "" : props.value}
                type={"text"}
                placeholder={placeholder}
                decimalScale={0}
                thousandSeparator
                isNumericString
                allowNegative={false}
                onBlur={props.onBlur}
                defaultValue={""}
                allowEmptyFormatting
                onValueChange={(e) => props.onChange(e.floatValue)}
                getInputRef={props.textRef}
            />
        );
    } else if (props.type === "textarea") {
        return (
            <textarea
                id={id}
                name={name}
                className={cx(baseClasses, styles["inputwrapper-textarea"])}
                value={props.value.toString()}
                placeholder={placeholder}
                onChange={props.onChange}
                onBlur={props.onBlur}
                rows={props.rows || 3}
                style={{
                    minHeight: 100
                }}
                maxLength={props.maxlength || 500}
                ref={props.textRef}
            />
        );
    } else {
        return (
            <input
                id={id}
                name={name}
                className={baseClasses}
                type={type}
                value={props.value}
                placeholder={placeholder}
                onChange={props.onChange}
                onBlur={props.onBlur}
                ref={props.textRef}
            />
        );
    }
};

const Input: React.FC<InputProps> = (props) => {
    const {
        label,
        children,
        hasCurrency,
        hasIcon,
        iconProps,
        hasError,
        tooltipContent,
        className,
        isDisabled,
        id
    } = props;

    return (
        <React.Fragment>
            <InputWrapper
                id={id + "--tooltip" }
                label={label}
                hasError={hasError}
                tooltipContent={tooltipContent}
                className={cx(
                    styles["inputwrapper"],
                    styles["inputwrapper--lightgreen"],
                    {
                        [styles["has-icon"]]: hasIcon,
                        [styles["has-currency"]]: hasCurrency,
                        [className]: !!className
                    }
                )}
                isDisabled={isDisabled}>
                {hasIcon && iconProps.iconName && (
                    <label
                        aria-label={label.toString()}
                        htmlFor={id + "--tooltip"}
                        className={cx({
                            [styles[iconProps.iconClassName]]: iconProps.iconClassName
                        })}>
                        <Icon
                            width={iconProps.iconWidth ?? "100%"}
                            height={iconProps.iconHeight ?? "100%"}
                            id={iconProps.iconName}
                        />
                    </label>
                )}
                {hasCurrency && (
                    <span
                        className={cx(
                            styles["inputwrapper-currency"],
                            "text-font-size-m"
                        )}>
                        $
                    </span>
                )}
                {renderInputElement(props)}
            </InputWrapper>
            {children}
        </React.Fragment>
    );
};

export default Input;
